import { usePathname } from 'next/navigation';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Configure,
  useClearRefinements,
  useCurrentRefinements,
  usePagination,
  useSortBy,
  useStats
} from 'react-instantsearch';
import { InstantSearchNext } from 'react-instantsearch-nextjs';

import { Breadcrumbs, PageNotFound } from '@/components/atomic/atoms';
import {
  Accordion,
  CategorySideBarMenu,
  RenderPagination
} from '@/components/atomic/molecules';
import { CloseIcon, Text } from '@/components/atomic/nuclei';
import {
  ProductListOrganism,
  RenderProductCardsOrganism
} from '@/components/atomic/organism';
import StandardLayout from '@/components/layout/StandardLayout';
import searchAPIConfig from '@/config/searchAPI';
import getSerializedRichText from '@/helpers/editor';
import createClient from '@/lib/instantSearchClient';
import { getCategoryListingPageH1Title } from '@/services/categories.service';
import { HOST_PORTAL_FQDN } from '@/services/connections.service';
import { getAuthToken } from '@/services/identity.service';
import DISCOUNT_ITEMS from '@/config/searchFilters';

const INDEX_NAME = 'products_weightage_desc';

const SORT_ITEMS = [
  { value: 'products_weightage_desc', label: 'Relevance' },
  { value: 'products_price_asc', label: 'Price: Low to High' },
  { value: 'products_price_desc', label: 'Price: High to Low' }
];

const DescriptionFAQBlock = ({ categoryName, description, faq }) => (
  <div className='flex flex-col gap-4 my-4 mx-20'>
    {getSerializedRichText(description) && (
      <Accordion
        {...{
          heading: `About ${categoryName}`,
          richText: description
        }}
      />
    )}
    {getSerializedRichText(faq) && (
      <Accordion
        {...{ heading: 'Frequently Asked Questions', richText: faq }}
      />
    )}
  </div>
);

function SelectedFilterItem({ label, onRemove }) {
  return (
    <li>
      <label className='h-9 flex justify-between items-center border border-platinum cursor-pointer px-4 rounded-md min-w-30 hover:bg-ghost-white'>
        <span className='text-dim-gray font-medium text-sm leading-17px capitalize me-3 select-none'>
          {label}
        </span>
        <CloseIcon
          onClick={onRemove}
          width={16}
          height={16}
        />
      </label>
    </li>
  );
}

function buildDisplayRefinements(activeRefinements, removeRefinement) {
  const result = [];
  for (const item of activeRefinements) {
    if (item.attribute === 'price.sortPrice') {
      const min = item.refinements.find((r) => r.label.startsWith('≥'));
      const max = item.refinements.find((r) => r.label.startsWith('≤'));
      const minVal = min?.label.replace(/≥\s*/, '');
      const maxVal = max?.label.replace(/≤\s*/, '');
      const priceKey = `${minVal ? 'min' : ''}_${maxVal ? 'max' : ''}`;
      const priceLabel = {
        min_max: `AED ${minVal} - AED ${maxVal}`,
        min_: `From AED ${minVal}`,
        _max: `Up to AED ${maxVal}`,
        _: item.refinements.map((r) => r.label).join(' - ')
      };
      const label = priceLabel[priceKey];
      result.push({
        key: 'price.sortPrice',
        label,
        onRemove: () => item.refinements.forEach((r) => removeRefinement(r))
      });
    } else if (item.attribute === 'isInstantPrice') {
      result.push({
        key: 'isInstantPrice',
        label: 'Instant Price Products',
        onRemove: () => item.refinements.forEach((r) => removeRefinement(r))
      });
    } else if (item.attribute === 'discount') {
      const hasEnd = item.refinements.some((r) => r.label.startsWith('≤'));
      const hasStart = item.refinements.some((r) => r.label.startsWith('≥'));
      const endRefinement = item.refinements.find((r) =>
        r.label.startsWith('≤')
      );
      const startRefinement = item.refinements.find((r) =>
        r.label.startsWith('≥')
      );
      const endVal = endRefinement
        ? Number(endRefinement.label.replace(/≤\s*/, ''))
        : null;
      const startVal = startRefinement
        ? Number(startRefinement.label.replace(/≥\s*/, ''))
        : null;
      const matched = DISCOUNT_ITEMS.find((d) => {
        if (hasEnd && !hasStart)
          return d.end === endVal && d.start === undefined;
        if (hasStart && !hasEnd)
          return d.start === startVal && d.end === undefined;
        return d.start === startVal && d.end === endVal;
      });
      result.push({
        key: 'discount',
        label: matched
          ? matched.label
          : item.refinements.map((r) => r.label).join(' - '),
        onRemove: () => item.refinements.forEach((r) => removeRefinement(r))
      });
    } else {
      item.refinements.forEach((r) => {
        result.push({
          key: `${r.attribute}-${r.label}`,
          label: r.label,
          onRemove: () => removeRefinement(r)
        });
      });
    }
  }
  return result;
}

const ClientSideProductList = ({
  breadcrumbsInfo,
  categories,
  category,
  categorySlug,
  description,
  faq,
  parentCategoryName,
  parentSlug,
  priceRange,
  productListingRef,
  productsCount,
  subCategorySlug
}) => {
  const { nbHits } = useStats();
  const { currentRefinement: currentSort, refine: refineSort } = useSortBy({
    items: SORT_ITEMS
  });
  const { items: activeRefinements, refine: removeRefinement } =
    useCurrentRefinements();
  const { refine: clearAllRefinements } = useClearRefinements();
  const {
    currentRefinement: currentPage,
    nbPages,
    isFirstPage,
    isLastPage,
    refine: refinePage
  } = usePagination();

  const safeNbPages = nbPages || 0;
  const padding = 3;
  const pageStart = Math.max(0, currentPage - padding);
  const pageEnd = Math.min(safeNbPages - 1, currentPage + padding);
  const pages = Array.from(
    { length: Math.max(0, pageEnd - pageStart + 1) },
    (_, i) => pageStart + i
  );

  const allRefinements = buildDisplayRefinements(
    activeRefinements,
    removeRefinement
  );

  if (productsCount < 1) {
    return (
      <div>
        <PageNotFound
          {...{
            message: 'Product(s) Not Found',
            subMessage1: 'No products found in category.'
          }}
        />
        <DescriptionFAQBlock
          {...{
            categoryName: category.name,
            description,
            faq
          }}
        />
      </div>
    );
  }

  const RenderProductsCount = () => (
    <div
      className='my-4 ms-1'
      data-qa='hits-stats'
    >
      <Text
        {...{
          className:
            'inline-block font-medium text-base font-Montserrat text-nero',
          content: getCategoryListingPageH1Title(category),
          HtmlTag: 'h1'
        }}
      />
      <Text
        {...{
          className: 'font-normal text-base font-Montserrat text-dim-gray',
          content: ` - ${nbHits} products`,
          HtmlTag: 'span'
        }}
      />
    </div>
  );

  const renderPagination = (props) => {
    if (props.label !== '...') {
      return (
        <RenderPagination
          {...{
            ...props,
            onClick: () => {
              props.onClick();
              window.scrollTo(0, 0);
            }
          }}
        />
      );
    }
    return '';
  };

  const renderPageNumbers = () => {
    if (safeNbPages <= 1) return null;
    return (
      <div className='mt-6 py-4 border-t border-gray flex items-center justify-center font-Montserrat font-medium flex-row'>
        {renderPagination({
          label: 'Previous',
          onClick: () => !isFirstPage && refinePage(currentPage - 1),
          active: false,
          disabled: isFirstPage
        })}
        {pages.map((page) => (
          <RenderPagination
            key={page}
            label={String(page + 1)}
            onClick={() => {
              refinePage(page);
              window.scrollTo(0, 0);
            }}
            active={page === currentPage}
            disabled={false}
          />
        ))}
        {renderPagination({
          label: 'Next',
          onClick: () => !isLastPage && refinePage(currentPage + 1),
          active: false,
          disabled: isLastPage
        })}
      </div>
    );
  };

  return (
    <div>
      <div
        className='mt-6 z-0 flex px-5 md:px-20 scroll-mt-40'
        id='product-list'
        ref={productListingRef}
      >
        <CategorySideBarMenu
          {...{
            categories,
            categorySlug,
            currentCategory: category,
            parentCategoryName,
            parentSlug,
            priceRange,
            subCategorySlug
          }}
        />
        <div className='w-full'>
          <Breadcrumbs {...{ breadCrumbs: breadcrumbsInfo }} />
          <RenderProductsCount />
          <div className='flex space-x-7 mb-4 ms-1'>
            <Text
              {...{
                className: 'text-nero text-sm font-medium',
                content: 'Sort By'
              }}
            />
            <div className='ms-1 flex space-x-5 list-none text-sm font-medium'>
              {SORT_ITEMS.map((item) => (
                <button
                  key={item.value}
                  type='button'
                  className={`cursor-pointer ${
                    currentSort === item.value ? 'text-cinnabar' : 'text-nero'
                  }`}
                  onClick={() => refineSort(item.value)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
          <div className='flex'>
            <ul className='ms-1 flex list-none space-x-4 mb-4'>
              {allRefinements.map((r) => (
                <SelectedFilterItem
                  key={r.key}
                  label={r.label}
                  onRemove={r.onRemove}
                />
              ))}
            </ul>
            {allRefinements.length > 0 && (
              <div
                className='ms-4'
                onClick={clearAllRefinements}
              >
                <label className='h-9 flex justify-center items-center border border-platinum cursor-pointer px-4 rounded-md min-w-23 hover:bg-ghost-white'>
                  <span className='text-coral font-medium text-sm leading-17px capitalize select-none'>
                    {' '}
                    Clear All{' '}
                  </span>
                </label>
              </div>
            )}
          </div>
          <ProductListOrganism />
          {renderPageNumbers()}
        </div>
      </div>
      <DescriptionFAQBlock
        {...{
          categoryName: category.name,
          description,
          faq
        }}
      />
    </div>
  );
};

const ServerSideList = ({
  breadcrumbsInfo,
  categoryName,
  description,
  faq,
  initialProducts,
  productListingRef,
  productsCount,
  RenderProductsCount
}) => (
  <div
    className='mt-6 z-0 flex px-5 md:px-20 scroll-mt-40'
    id='product-list'
    ref={productListingRef}
  >
    <div className='w-full'>
      <Breadcrumbs {...{ breadCrumbs: breadcrumbsInfo }} />
      <Fragment>
        <RenderProductsCount {...{ hitsCount: productsCount }} />
        <RenderProductCardsOrganism {...initialProducts} />
        <DescriptionFAQBlock
          {...{
            categoryName,
            description,
            faq
          }}
        />
      </Fragment>
    </div>
  </div>
);

const CategoryDesktopLayoutTemplate = ({
  breadcrumbsInfo,
  categories,
  category,
  categorySeoDetails,
  categorySlug,
  initialProducts,
  parentCategories,
  parentCategoryName,
  parentSlug,
  priceRange,
  productsCount,
  subCategorySlug
}) => {
  const pathname = usePathname();
  const productListingRef = useRef(null);
  useEffect(() => {
    productListingRef?.current?.scrollIntoView();
  }, []);

  const { description, faq } = categorySeoDetails;

  const [showHeaderEventList, setShowHeaderEventList] = useState(true);
  const [isServerSide, setIsServerSide] = useState(true);

  useEffect(() => {
    setShowHeaderEventList(pathname !== '/categories/food-and-beverages');
  }, [pathname]);

  useEffect(() => {
    setIsServerSide(false);
  }, []);

  const searchClient = useMemo(
    () =>
      createClient({
        headers: () => ({
          'x-access-token': getAuthToken(),
          'x-origin-domain': HOST_PORTAL_FQDN
        }),
        url: searchAPIConfig.productListAPIEndPoint
      }),
    []
  );

  const categoryFilter = useMemo(() => {
    const slug = subCategorySlug || categorySlug || parentSlug;
    return `assignedToCategories:${slug}`;
  }, [subCategorySlug, categorySlug, parentSlug]);

  const RenderProductsCount = ({ hitsCount }) => (
    <div
      className='my-4 ms-1'
      data-qa='hits-stats'
    >
      <Text
        {...{
          className:
            'inline-block font-medium text-base font-Montserrat text-nero',
          content: getCategoryListingPageH1Title(category),
          HtmlTag: 'h1'
        }}
      />
      <Text
        {...{
          className: 'font-normal text-base font-Montserrat text-dim-gray',
          content: ` - ${hitsCount} products`,
          HtmlTag: 'span'
        }}
      />
    </div>
  );

  return (
    <Fragment>
      <StandardLayout
        key={pathname}
        {...{
          headParameters: {
            categories: parentCategories,
            categoriesWithSubCategories: categories,
            heading: 'Categories',
            parentCategories,
            parentSlug,
            showFooter: true,
            showHeaderEventList
          }
        }}
      >
        {isServerSide ? (
          <ServerSideList
            {...{
              breadcrumbsInfo,
              categoryName: category.name,
              description,
              faq,
              initialProducts,
              productListingRef,
              productsCount,
              RenderProductsCount
            }}
          />
        ) : (
          <InstantSearchNext
            indexName={INDEX_NAME}
            searchClient={searchClient}
            routing={true}
          >
            <Configure
              filters={categoryFilter}
              hitsPerPage={15}
            />
            <ClientSideProductList
              {...{
                breadcrumbsInfo,
                categories,
                category,
                categorySlug,
                description,
                faq,
                parentCategoryName,
                parentSlug,
                priceRange,
                productListingRef,
                productsCount,
                subCategorySlug
              }}
            />
          </InstantSearchNext>
        )}
      </StandardLayout>
    </Fragment>
  );
};

export default CategoryDesktopLayoutTemplate;
